import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { getAPIBaseURL } from '../../services/helpers'
import { getData, patchData } from '../../services/request'

const AdminEmployers = () => {
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [query, setQuery] = useState('')

  const fetchUsers = async () => {
    setLoading(true)
    setError('')
    const url = `${getAPIBaseURL()}/employme/admin/users?role=EMPLOYER`
    const res = await getData(url, {}, {})
    setLoading(false)
    if (res.error === '') {
      if (res.result?.status === 200) {
        const data = res.result.data?.data || res.result.data
        setUsers(Array.isArray(data) ? data : [])
      } else {
        setUsers([])
        setError(res.result?.data?.message || res.result?.data?.data || 'Unable to load employers')
      }
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Network error')
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const fullName = (u) => `${u.firstName || ''} ${u.lastName || ''}`.trim() || '—'

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return users
    return users.filter((u) => (
      fullName(u).toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.phoneNumber || '').toLowerCase().includes(q) ||
      (u.status || '').toLowerCase().includes(q)
    ))
  }, [users, query])

  const handleToggleStatus = async (user) => {
    const newStatus = user.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE'
    const url = `${getAPIBaseURL()}/employme/admin/users/${user._id}/status`
    const headers = { 'Content-Type': 'application/json' }
    const res = await patchData(url, headers, { status: newStatus })
    if (res.error === '' && res.result?.status === 200) {
      setUsers((prev) => prev.map((u) => (u._id === user._id ? { ...u, status: newStatus } : u)))
    } else {
      setError(res.result?.data?.message || res.result?.data?.data || 'Failed to update status')
    }
  }

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch {
      return iso || '—'
    }
  }

  return (
    <section className="mx-auto max-w-6xl">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employers</h1>
          <p className="text-sm text-gray-600">View and manage employer accounts</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search employers (name, email, phone)"
            className="w-72 max-w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:outline-none focus:ring-2 focus:ring-black"
          />
          <Link
            to="/admin/app/employme"
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Back to overview
          </Link>
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">
            {query.trim() ? 'No employers match your search' : 'No employers found'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 font-semibold text-gray-700">Name</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Email</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Phone</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Joined</th>
                  <th className="px-4 py-3 font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((u) => (
                  <tr key={u._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <Link to={`/admin/app/employme/employers/${u._id}`} className="font-medium text-gray-900 hover:underline">
                        {fullName(u)}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{u.email || '—'}</td>
                    <td className="px-4 py-3 text-gray-700">{u.phoneNumber || '—'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                          u.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {u.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(u.createdAt)}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleStatus(u)}
                        className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                          u.status === 'ACTIVE'
                            ? 'border border-red-600 text-red-600 hover:bg-red-50'
                            : 'border border-green-600 text-green-600 hover:bg-green-50'
                        }`}
                      >
                        {u.status === 'ACTIVE' ? 'Suspend' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  )
}

export default AdminEmployers
